import React from "react";
import { Badge, Box, Button, Text, Tooltip } from "@chakra-ui/react";
import { getBaseUrl } from "../flow/engine/apiClient";

export interface EnvInfo {
  baseUrl: string;
  host: string;
  isUat: boolean;
  isLocal: boolean;
  /** True when the tester may run without an explicit confirmation. */
  isSafe: boolean;
}

export const getEnvInfo = (): EnvInfo => {
  const baseUrl = getBaseUrl();
  let host = baseUrl;
  try {
    host = new URL(baseUrl).host;
  } catch {
    // keep raw
  }
  const isUat = /uat|staging|stage|dev|test/i.test(host);
  const isLocal = /localhost|127\.0\.0\.1|0\.0\.0\.0/i.test(host);
  return { baseUrl, host, isUat, isLocal, isSafe: isUat || isLocal };
};

const CONFIRM_KEY = "ORCA_FLOW_TESTER_ENV_CONFIRMED_HOST";

export const isEnvConfirmed = (env: EnvInfo) =>
  env.isSafe || sessionStorage.getItem(CONFIRM_KEY) === env.host;

export const EnvironmentBadge: React.FC<{ env: EnvInfo }> = ({ env }) => (
  <Tooltip label={env.baseUrl} hasArrow>
    <Badge colorScheme={env.isLocal ? "gray" : env.isUat ? "green" : "red"} variant="solid" fontSize="10px" px={2} py={0.5} borderRadius="md">
      {env.isLocal ? "LOCAL" : env.isUat ? "UAT" : "PRODUCTION"} · {env.host}
    </Badge>
  </Tooltip>
);

export const EnvironmentGuard: React.FC<{ env: EnvInfo; onConfirmed: () => void }> = ({ env, onConfirmed }) => {
  if (isEnvConfirmed(env)) return null;
  return (
    <Box borderWidth="1px" borderColor="red.300" bg="red.50" borderRadius="lg" p={4}>
      <Text fontWeight="700" fontSize="sm" color="red.700" mb={1}>
        Production backend
      </Text>
      <Text fontSize="xs" color="red.700" mb={3}>
        The tester is pointed at <b>{env.baseUrl}</b>. Orders and transitions on this backend are real. Confirm to allow execution in this browser session.
      </Text>
      <Button
        size="sm"
        colorScheme="red"
        onClick={() => {
          sessionStorage.setItem(CONFIRM_KEY, env.host);
          onConfirmed();
        }}
      >
        I understand, continue
      </Button>
    </Box>
  );
};
